import * as React from "react";
import { Avatar, Badge, Box, Stack, Tooltip, Typography } from "@mui/material";
import { getRankBadge } from "../config/rankBadges";
import { getRankNameFromPoints } from "../config/ranksUtil";

export type PlayerIdentityRowProps = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;

  points?: number | null;

  avatarUrl?: string | null;

  onClick?: () => void;

  size?: "sm" | "md" | "lg" | "xs";

  specialSize?: number | null;

  /**
   * Punkte anzeigen (Default: false)
   */
  showPoints?: boolean;

  /**
   * Namen anzeigen (Default: true)
   */
  showName?: boolean;
};

const AVATAR_SIZE: Record<NonNullable<PlayerIdentityRowProps["size"]>, number> = {
  xs: 25,
  sm: 32,
  md: 40,
  lg: 56,
};

export const makeAbsolute = (url?: string | null) => {
  if (!url) return undefined;

  // ✅ Preview URLs dürfen NICHT umgeschrieben werden
  if (url.startsWith("blob:") || url.startsWith("data:")) return url;

  // ✅ normale absolute URLs
  if (url.startsWith("http://") || url.startsWith("https://")) return url;

  // ✅ relative CDN-Pfade
  return `${process.env.REACT_APP_CDN_URL ?? ""}/static/avatars/${url}`;
};


export const initials = (p: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}) => {
  const base =
    `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() ||
    p.email ||
    "?";
  return base
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

function fmtPoints(points?: number | null) {
  if (points == null) return "";
  return points.toLocaleString("de-DE");
}

/**
 * Einheitliche Spieler-Zeile:
 *
 * - Avatar mit Rank-Badge-Overlay
 * - optional Name (mobil: Vor-/Nachname getrennt)
 * - optional Punkte
 *
 * Sehr kompakt nutzbar durch `showName={false}`
 */
export default function PlayerIdentityRow(props: PlayerIdentityRowProps) {
  const {
    firstName,
    lastName,
    email,
    points,
    avatarUrl,
    onClick,
    size = "md",
    specialSize,
    showPoints = false,
    showName = true,
  } = props;

  const absAvatar = makeAbsolute(avatarUrl);

  const rankName = getRankNameFromPoints(points);
  const badge = getRankBadge(rankName);

  const avatarPx = AVATAR_SIZE[size];

  const badgePx = size === "lg" ? 22 : 18;
  const badgePxMobile = 16;


  const fallbackName = email || "Unbekannt";
  const fullName =
    `${firstName ?? ""} ${lastName ?? ""}`.trim() || fallbackName;

  const content = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={{ xs: 1, sm: 1.25 }}
      sx={{
        width: "100%",
        minWidth: 0,
        overflow: "visible",
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
      }}
      onClick={onClick}
    >
      {/* Avatar + Rank Badge */}
      <Badge
  overlap="circular"
  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
  badgeContent={
    badge?.icon ? (
      <Box
        component="img"
        src={badge.icon}
        alt={badge.label ?? rankName}
        sx={{
          width: { xs: 18, sm: size === "lg" ? 26 : 22 },
          height: { xs: 18, sm: size === "lg" ? 26 : 22 },

          // ✨ kein Hintergrund, kein Rahmen
          bgcolor: "transparent",
          p: 0,

          // ✨ leicht versetzt (rechts + runter)
          transform: "translate(10%, 10%)",

          // ✨ optional: leichter Schatten für Lesbarkeit
          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",

          display: "block",
          pointerEvents: "none",
        }}
      />
    ) : null
  }
  sx={{
    "& .MuiBadge-badge": {
      minWidth: "auto",
      height: "auto",
      padding: 0,
      borderRadius: "50%",
      background: "transparent",
    },
  }}
>
        <Avatar
  src={absAvatar}
  sx={{
    width: { xs: 32, sm: avatarPx },
    height: { xs: 32, sm: avatarPx },
    fontSize: { xs: 13, sm: size === "lg" ? 18 : 14 },
    fontWeight: 800,
    bgcolor: "primary.main",
    flexShrink: 0,

    // 🔥 Rank-Rand
    boxShadow: badge?.primary
      ? `
        0 0 0 2px ${badge.primary},
        inset 0 0 0 1px rgba(255,255,255,0.25)
      `
      : undefined,
  }}
>
  {initials({ firstName, lastName, email })}
</Avatar>
      </Badge>

      {/* Name + Punkte (optional) */}
      {showName && (
        <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
          {/* Mobile: 2 Zeilen */}
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {firstName || fallbackName}
            </Typography>

            {lastName && (
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {lastName}
              </Typography>
            )}
          </Box>

          {/* Desktop: eine Zeile */}
          <Typography
            variant={size === "lg" ? "h6" : "subtitle1"}
            sx={{
              display: { xs: "none", sm: "block" },
              fontWeight: 800,
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={fullName}
          >
            {fullName}
          </Typography>

          {showPoints && points != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontWeight: 700,
                lineHeight: 1.1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {fmtPoints(points)} Punkte
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );

  // Wenn kein Name angezeigt wird, trotzdem Tooltip mit Name + Rank
  if (!showName) {
    return (

        <Box>{content}</Box>

    );
  }

  return content;
}
